/**
 * Último atalho usado e atalho fixado (`user_settings.chart_preferences`).
 */
import { isScreenshotFixtureMode } from "@/shared/lib/screenshotMode";
import { toRepositoryError } from "@/shared/repositories/common/repositoryError";
import {
  getCurrentUserId,
  toJsonObject,
} from "@/shared/repositories/settings/userSettingsCommon";
import { supabase } from "@/shared/supabase/client";
import type { Json } from "@/shared/supabase/database.types";

export const quickActionsRepository = {
  getLastQuickActionId: async (): Promise<string | null> => {
    if (isScreenshotFixtureMode()) {
      return "quick-action-new-exam";
    }
    try {
      const userId = await getCurrentUserId();
      const { data, error } = await supabase
        .from("user_settings")
        .select("chart_preferences")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        throw toRepositoryError(
          error,
          "Não foi possível carregar preferências.",
        );
      }

      const prefs = toJsonObject(data?.chart_preferences);
      const id = prefs.lastQuickActionId ?? prefs.last_quick_action_id;
      return typeof id === "string" && id.length > 0 ? id : null;
    } catch (error) {
      throw toRepositoryError(error, "Falha ao carregar último atalho usado.");
    }
  },

  updateLastQuickActionId: async (quickActionId: string): Promise<void> => {
    try {
      const userId = await getCurrentUserId();
      const { data, error } = await supabase
        .from("user_settings")
        .select("chart_preferences")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        throw toRepositoryError(
          error,
          "Não foi possível carregar preferências atuais.",
        );
      }

      const currentPreferences = toJsonObject(data?.chart_preferences);
      const mergedPreferences: Json = {
        ...currentPreferences,
        lastQuickActionId: quickActionId,
      };

      const { error: saveError } = await supabase.from("user_settings").upsert(
        {
          user_id: userId,
          chart_preferences: mergedPreferences,
        },
        { onConflict: "user_id" },
      );

      if (saveError) {
        throw toRepositoryError(
          saveError,
          "Não foi possível salvar último atalho usado.",
        );
      }
    } catch (error) {
      throw toRepositoryError(error, "Falha ao salvar último atalho usado.");
    }
  },

  getPinnedQuickActionId: async (): Promise<string | null> => {
    if (isScreenshotFixtureMode()) {
      return "quick-action-new-document";
    }
    try {
      const userId = await getCurrentUserId();
      const { data, error } = await supabase
        .from("user_settings")
        .select("chart_preferences")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        throw toRepositoryError(
          error,
          "Não foi possível carregar preferências.",
        );
      }

      const prefs = toJsonObject(data?.chart_preferences);
      const id = prefs.pinnedQuickActionId ?? prefs.pinned_quick_action_id;
      return typeof id === "string" && id.length > 0 ? id : null;
    } catch (error) {
      throw toRepositoryError(error, "Falha ao carregar atalho fixado.");
    }
  },

  updatePinnedQuickActionId: async (
    quickActionId: string | null,
  ): Promise<void> => {
    try {
      const userId = await getCurrentUserId();
      const { data, error } = await supabase
        .from("user_settings")
        .select("chart_preferences")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        throw toRepositoryError(
          error,
          "Não foi possível carregar preferências atuais.",
        );
      }

      const currentPreferences = toJsonObject(data?.chart_preferences);
      const mergedPreferences: Json = {
        ...currentPreferences,
        pinnedQuickActionId: quickActionId,
      };

      const { error: saveError } = await supabase.from("user_settings").upsert(
        {
          user_id: userId,
          chart_preferences: mergedPreferences,
        },
        { onConflict: "user_id" },
      );

      if (saveError) {
        throw toRepositoryError(
          saveError,
          "Não foi possível salvar atalho fixado.",
        );
      }
    } catch (error) {
      throw toRepositoryError(error, "Falha ao salvar atalho fixado.");
    }
  },
};
